import { ADDON_KEYS, type AddonKey, type AddonMap, type AttributionKey, type TenantDetail } from '@/api/types'

/** The six add-ons, in display order (1.0's Forms was retired at registration step R). */
export const ADDONS: Record<AddonKey, { label: string; description: string }> = {
  scorebook: { label: 'Scorebook', description: 'Tablet stat entry with play-by-play' },
  broadcast: { label: 'Broadcast', description: 'Score overlays, templates, live events' },
  fan_ai: { label: 'Fan AI', description: 'Public chat assistant for fans' },
  ticketing: { label: 'Ticketing', description: 'Online and door sales through Stripe Connect' },
  white_label: { label: 'White-label', description: 'Custom domain; lets you hide the ScoreLM credits' },
  registration: {
    label: 'Registration',
    description: 'Player accounts and season sign-ups on the league site. In development: turn on only for leagues testing it.',
  },
}

export const ATTRIBUTION_LABELS: Record<AttributionKey, string> = {
  footer: 'Footer credit',
  share_cards: 'Share-card watermark',
  corner_badge: 'Corner badge',
}

export const ATTRIBUTION_DESCRIPTIONS: Record<AttributionKey, string> = {
  footer: '"Powered by ScoreLM" in the public site footer',
  share_cards: 'ScoreLM mark on shared score and stat cards',
  corner_badge: 'Small ScoreLM badge in the corner of the public site',
}

export function enabledAddons(addons: AddonMap): AddonKey[] {
  return ADDON_KEYS.filter((k) => addons[k])
}

const NONE: AddonMap = { scorebook: false, broadcast: false, fan_ai: false, ticketing: false, white_label: false, registration: false }

/**
 * What each package grants by itself, until the server sends
 * `tenant.package_addons`. "standard" mirrors what the backend's package row
 * grants: broadcast, fan_ai, white_label. Migration 086 still stores 'forms'
 * in it, and the server ignores that key since Forms were retired at
 * registration step R. "early_access" is the package key 086 renamed to
 * "standard"; no database has it any more, but the entry keeps a stale
 * response from reading as "no add-ons".
 */
export const PACKAGE_ADDON_DEFAULTS: Record<string, AddonMap> = {
  standard: { ...NONE, broadcast: true, fan_ai: true, white_label: true },
  early_access: { ...NONE, broadcast: true, fan_ai: true },
}

/** The add-on map "Reset to package default" sends for this tenant. */
export function packageDefaultAddons(tenant: Pick<TenantDetail, 'account' | 'package_addons'>): AddonMap {
  if (tenant.package_addons) return tenant.package_addons
  const key = tenant.account.package_key
  return (key && PACKAGE_ADDON_DEFAULTS[key]) || NONE
}

/** Keys whose current value differs from the package default. */
export function addonsDifferingFrom(addons: AddonMap, defaults: AddonMap): AddonKey[] {
  return ADDON_KEYS.filter((k) => addons[k] !== defaults[k])
}

/**
 * Whether White-label is in effect: turned on AND the account is live. Only
 * then do the stored attribution settings apply; otherwise the public site
 * uses the defaults (footer credit shown, watermark and corner badge hidden).
 */
export function whiteLabelInEffect(tenant: Pick<TenantDetail, 'addons' | 'account'>): boolean {
  return tenant.addons.white_label && tenant.account.is_live
}
